import * as React from 'react'
import { Text, TouchableOpacity, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import SignaturePad from '@components/signatures/SignaturePad'
import Root from '@components/root/Root'
import Header from '@components/header/Header'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

// Mobile render with only signature
const FormSignatureLayout = ({ title, name, type, onForm, onSceneChange, onBack, form }: SignatureLayoutProps) => {
  const pad = React.useRef<React.ElementRef<typeof SignaturePad>>(null)

  const i18n = useI18n()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [submitting, setSubmitting] = React.useState(false)

  const submit = () => {
    if (pad.current) {
      pad.current.save()
    }
  }

  const onSave = (image: string) => {
    onForm({ ...form, signature: image })
      .then(() => onSceneChange('GLOBAL'))
      .finally(() => setSubmitting(true))
  }

  return (
    <Root
      fixed
      header={title}
      onLeft={onBack}
      rightText={i18n.t('screens.edlSignature.clear')}
      onRight={() => pad.current?.reset()}>
      {!isPhone ? (
        <ScrollView bounces={false} contentContainerStyle={{ flexGrow: 1 }}>
          <Header
            title={i18n.t('screens.edlSignature.signatureTitle', { type: i18n.t(`screens.edlRecap.${type}`) })}
            description={name}
          />
          <Description>
            {i18n.t('screens.edlSignature.signatureDescription', { type: i18n.t(`screens.edlRecap.${type}`) })}
          </Description>
          <Column>
            <Title>{i18n.t('screens.edlSignature.stepTitle')}</Title>
            <Signature>
              <SignaturePad ref={pad} onSave={onSave} />
              <Clear activeOpacity={0.8} onPress={() => pad.current?.reset()}>
                <ClearText>{i18n.t('screens.edlSignature.clear')}</ClearText>
              </Clear>
            </Signature>
          </Column>
        </ScrollView>
      ) : (
        <SignaturePhone>
          <SignaturePad ref={pad} onSave={onSave} />
        </SignaturePhone>
      )}

      <Buttons>
        <ButtonContainer>
          <Button
            textOnly
            textColor={theme.colors.primary}
            label={i18n.t('common.cancel')}
            status="active"
            onClick={() => onSceneChange('GLOBAL')}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button label={i18n.t('common.validate')} status={submitting ? 'loading' : 'active'} onClick={submit} />
        </ButtonContainer>
      </Buttons>
    </Root>
  )
}

export default FormSignatureLayout

const Column = styled(View)`
  flex: 1;
  flex-direction: column;
  margin: 30px 60px;
`

const Buttons = styled(View)`
  flex-direction: row;
  ${(props) => props.theme.breakpoint !== 'phone' && 'justify-content: center'};
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'flex: 1' : 'width: 275px')};
  margin: 12px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
  text-align: center;
`

const Description = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.tertiary};
  text-align: center;
  margin: 40px;
`

const Signature = styled(View)`
  height: 300px;
  align-self: center;
  width: ${(props) =>
    props.theme.breakpoint === 'phone'
      ? 275
      : props.theme.breakpoint === 'medium'
      ? 400
      : props.theme.breakpoint === 'mediumBig'
      ? 600
      : 1000}px;
  margin: 0 150px;
  background-color: ${(props) => props.theme.colors.background};
`

const SignaturePhone = styled(View)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const Clear = styled(TouchableOpacity)`
  position: absolute;
  right: 0;
  top: 0;
  border-radius: 4px;
  margin: 10px;
  padding: 8px;
  background-color: ${(props) => props.theme.colors.primaryFocused};
`

const ClearText = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
`
